<script setup lang="ts">
import { MContainerRow, useAssets } from "@xlabmedical/medic-web-framework";
import { computed } from "vue";
import { useProductionCenterStore } from "@/stores/ProductionCenterStore";
import { usePatientsStore } from "@/stores/PatientsStore";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import { getVisibleIconCount } from "@/util/PatientListHelpers";

const patient = defineModel<PatientListModel>("patient", { required: true });
const props = defineProps<{ maxStateIconsCount: number }>();

const assets = useAssets();
const toast = useToast();
const router = useRouter();
const { t } = useI18n();

const productionCenterStore = useProductionCenterStore();
const { productionCenterList } = storeToRefs(productionCenterStore);

const patientStore = usePatientsStore();
const { projects } = storeToRefs(patientStore);

const sharedStateTooltipText = computed(() => {
    const tooltipParts: string[] = [];

    if (patient.value.foreignOwner) {
        tooltipParts.push(`${t("web-rg.sharedBy")}: ${patient.value.foreignOwner}`);
    }

    if (patient.value.shares.length > 0) {
        const users = [...new Set(patient.value.shares.map((share) => share.withUser.name))].join(", ");
        tooltipParts.push(`${t("web-rg.sharedWith")}: ${users}`);
    }

    return tooltipParts.join(" | ");
});

const hasSharingState = computed(() => {
    return !!patient.value.foreignOwner || patient.value.shares.length > 0;
});

const onOpenWeblinkOrderClick = async () => {
    const project = projects?.value.find((p) => p.weblinkOrderData?.projectId === patient.value.orderProjectId);

    if (project) {
        const pcId = project.weblinkOrderData?.productionCenterId;
        const pcUrl = productionCenterList.value.find((p) => p.id === pcId)?.url;

        const routeUrl = router.resolve({
            name: "OpenWeblinkOrder",
            params: {
                projectName: project.weblinkOrderData?.weblinkProjectName,
                productionCenterName: pcUrl,
            },
        }).href;

        window.open(routeUrl, "_blank", "noopener");
    } else if (patient.value.hasWeblinkOrder) {
        toast.add({
            severity: "error",
            summary: "Error",
            detail: t("web-rg.errorWhileOpeningTheProjectProjectNotFound"),
            life: 3000,
        });
    }
};

const visibleIconCount = computed(() => getVisibleIconCount(patient.value));

const tooManyIcons = computed(() => {
    return visibleIconCount.value > 1;
});

const iconSize = computed(() => {
    return 24;
});
</script>

<template>
    <MContainerRow :class="['state-icons-row gap-1 justify-start', { 'justify-center': visibleIconCount === 1 && props.maxStateIconsCount === 1 }]">
        <img
            v-if="!patient.hasAccess"
            class="state-icon"
            :style="{ width: `${iconSize}px`, height: `${iconSize}px` }"
            :src="assets.getAssetUrl('/icons/rg_warning.svg')"
            v-tooltip="'Expired access'"
            alt="Expired access" />
        <img
            class="state-icon"
            :style="{ width: `${iconSize}px`, height: `${iconSize}px` }"
            :src="assets.getAssetUrl('/icons/ik_localCloud_02_128.png')"
            v-tooltip="$t('web-rg.cloudStudy')"
            alt="'img"
            @click="onOpenWeblinkOrderClick" />
        <RgPatientListIconBadge
            v-if="patient.projectUnreadNotificationsPerChannel"
            class="state-icon-badge"
            type="project"
            icon="/icons/ik_notifications_02_128.png"
            :count="patient.projectUnreadNotificationsPerChannel"
            :tooManyIcons="tooManyIcons" />
        <RgPatientListIconBadge
            v-if="patient.hasWeblinkOrder"
            class="state-icon-badge"
            type="order"
            :count="patient.orderUnreadNotificationsPerChannel"
            :tooManyIcons="tooManyIcons" />
        <img
            v-if="hasSharingState"
            class="state-icon"
            :style="{ width: `${iconSize}px`, height: `${iconSize}px` }"
            :src="assets.getAssetUrl('/icons/ik_shared_08_128.png')"
            v-tooltip="{ value: sharedStateTooltipText, class: 'rg-shared-with-tooltip' }"
            alt="'img" />
    </MContainerRow>
</template>

<style>
.state-icons-row {
    flex-wrap: nowrap !important;
    align-items: center;
    min-width: max-content;
}

.state-icons-row > * {
    flex: 0 0 auto;
}

.state-icon {
    flex: 0 0 24px;
}

.state-icon-badge {
    flex: 0 0 24px;
}

.rg-shared-with-tooltip.p-tooltip {
    max-width: min(32rem, calc(100vw - 2rem));
    z-index: 1100 !important;
}

.rg-shared-with-tooltip .p-tooltip-text {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
}

@media (max-width: 768px) {
    .rg-shared-with-tooltip.p-tooltip {
        max-width: 85vw;
    }
}
</style>
